import { createGlobalState, StorageSerializers, useStorage } from "@vueuse/core";
import { computed } from "vue";

export interface StoredAccount {
  index: number
  userId: number
  name: string
  username: string
  avatarUrl: string | null
  lastUsedAt: number
  /** Last location and organisation this account worked in, shown in the account menu and searched in the accounts dialog. */
  locationName?: string | null
  clientName?: string | null
  /** Set when the refresh token of this slot was rejected; cleared by the next successful login into the slot. */
  expired?: boolean
}

export const ACCOUNTS_STORAGE_KEY = "accounts";
export const ACTIVE_ACCOUNT_SESSION_KEY = "active-account";
export const AUTHUSER_QUERY_PARAM = "authuser";
export const REFRESH_TOKEN_KEY = "refresh-token";
export const ACCOUNT_SCOPED_KEYS = [REFRESH_TOKEN_KEY, "permissions", "user-permissions", "defaultLocation"] as const;

export function accountScopedKey(key: string, index: number): string {
  return `${key}.${index}`;
}

// Node (test setup, SSR) exposes no usable Web Storage; the store then degrades to a single
// in-memory account just like `useStorage` does without a backend.
function localStore(): Storage | null {
  return globalThis.localStorage ?? null;
}

function sessionStore(): Storage | null {
  return globalThis.sessionStorage ?? null;
}

function parseIndex(value: string | null | undefined): number | null {
  if (value === null || value === undefined || !/^\d+$/.test(value)) {
    return null;
  }
  return Number.parseInt(value, 10);
}

function readAuthuserFromLocation(): number | null {
  if (typeof window === "undefined") {
    return null;
  }
  return parseIndex(new URLSearchParams(window.location.search).get(AUTHUSER_QUERY_PARAM));
}

// Pre-multi-account installs stored the remembered refresh token in sessionStorage and the other
// account-bound keys unsuffixed in localStorage; they become slot 0 exactly once. The accounts
// key is written on the first boot of this store (`useStorage` persists its default), so its
// presence marks the migration as done: the new code keeps using sessionStorage `refresh-token`
// per tab, and re-running would copy another account's tab token into slot 0.
function migrateLegacyKeys(): void {
  const local = localStore();
  const session = sessionStore();
  if (!local || !session || local.getItem(ACCOUNTS_STORAGE_KEY) !== null) {
    return;
  }
  for (const key of ACCOUNT_SCOPED_KEYS) {
    const scoped = accountScopedKey(key, 0);
    if (local.getItem(scoped) !== null) {
      continue;
    }
    const source = key === REFRESH_TOKEN_KEY ? session : local;
    const legacy = source.getItem(key);
    if (legacy === null) {
      continue;
    }
    local.setItem(scoped, legacy);
    source.removeItem(key);
  }
}

export const useAccountsStore = createGlobalState(() => {
  migrateLegacyKeys();

  const accounts = useStorage<StoredAccount[]>(ACCOUNTS_STORAGE_KEY, [], localStore() ?? undefined, { serializer: StorageSerializers.object });

  // The query wins so `switchAccount` can re-target the current tab; the session mirror keeps the
  // choice across in-tab reloads once the router has stripped the query.
  const fromQuery = readAuthuserFromLocation();
  const fromSession = parseIndex(sessionStore()?.getItem(ACTIVE_ACCOUNT_SESSION_KEY));
  const activeIndex = fromQuery ?? fromSession ?? 0;
  sessionStore()?.setItem(ACTIVE_ACCOUNT_SESSION_KEY, String(activeIndex));

  const activeAccount = computed(() => accounts.value.find(a => a.index === activeIndex) ?? null);
  const otherAccounts = computed(() => accounts.value
    .filter(a => a.index !== activeIndex)
    .sort((a, b) => b.lastUsedAt - a.lastUsedAt || a.index - b.index));
  // The account a tab continues as when its own account leaves: an expired slot would only land on the login screen.
  const nextAccount = computed(() => otherAccounts.value.find(a => !a.expired) ?? null);

  function scopedKey(key: string): string {
    return accountScopedKey(key, activeIndex);
  }

  function readRefreshToken(index: number = activeIndex): string | null {
    return localStore()?.getItem(accountScopedKey(REFRESH_TOKEN_KEY, index)) ?? null;
  }

  function writeRefreshToken(token: string | null, index: number = activeIndex): void {
    const key = accountScopedKey(REFRESH_TOKEN_KEY, index);
    if (token === null) {
      localStore()?.removeItem(key);
      return;
    }
    localStore()?.setItem(key, token);
  }

  // Returns the slot this user already occupies when they signed in again through `addAccount`,
  // so the caller can switch there instead of storing the same user twice.
  function upsertActive(input: Omit<StoredAccount, "index" | "lastUsedAt">): number {
    const existing = accounts.value.find(a => a.userId === input.userId && a.index !== activeIndex);
    if (existing) {
      // The caller hands a fresh token to that slot, so it is no longer expired.
      clearExpired(existing.index);
      return existing.index;
    }
    const previous = accounts.value.find(a => a.index === activeIndex);
    const next = accounts.value.filter(a => a.index !== activeIndex);
    // A new entry has no `expired` flag, which is how a successful login clears it.
    next.push({
      ...input,
      locationName: previous?.userId === input.userId ? previous.locationName : null,
      clientName: previous?.userId === input.userId ? previous.clientName : null,
      index: activeIndex,
      lastUsedAt: Date.now(),
    });
    accounts.value = next.sort((a, b) => a.index - b.index);
    return activeIndex;
  }

  function removeAccount(index: number): void {
    accounts.value = accounts.value.filter(a => a.index !== index);
    for (const key of ACCOUNT_SCOPED_KEYS) {
      localStore()?.removeItem(accountScopedKey(key, index));
    }
  }

  function removeAllAccounts(): void {
    for (const account of accounts.value) {
      for (const key of ACCOUNT_SCOPED_KEYS) {
        localStore()?.removeItem(accountScopedKey(key, account.index));
      }
    }
    accounts.value = [];
  }

  function patchAccount(index: number, patch: Partial<StoredAccount>): void {
    if (!accounts.value.some(a => a.index === index)) {
      return;
    }
    accounts.value = accounts.value.map(a => (a.index === index ? { ...a, ...patch } : a));
  }

  function markExpired(index: number = activeIndex): void {
    if (accounts.value.find(a => a.index === index)?.expired === true) {
      return;
    }
    patchAccount(index, { expired: true });
  }

  function clearExpired(index: number = activeIndex): void {
    if (!accounts.value.find(a => a.index === index)?.expired) {
      return;
    }
    accounts.value = accounts.value.map((a) => {
      const { expired: _expired, ...rest } = a;
      return a.index === index ? rest : a;
    });
  }

  // Skips the write when nothing changed: the storage ref would otherwise notify every tab on each navigation.
  function updateActiveContext(context: { locationName: string | null, clientName: string | null }): void {
    const current = activeAccount.value;
    if (!current) {
      return;
    }
    if ((current.locationName ?? null) === context.locationName && (current.clientName ?? null) === context.clientName) {
      return;
    }
    patchAccount(activeIndex, context);
  }

  function nextFreeIndex(): number {
    let index = 0;
    while (accounts.value.some(a => a.index === index)) {
      index += 1;
    }
    return index;
  }

  return { accounts, activeIndex, activeAccount, otherAccounts, nextAccount, scopedKey, readRefreshToken, writeRefreshToken, upsertActive, removeAccount, removeAllAccounts, markExpired, clearExpired, updateActiveContext, nextFreeIndex };
});
